<script>
  import { Callout, Menu, MenuItem } from "@gl/design-system";
  import { navigate } from "svelte-routing";
  import { getContext } from "svelte";
  import {
    faBook,
    faDiagramProject,
    faPalette,
    faScissors,
    faShieldHalved,
    faTowerBroadcast,
  } from "@fortawesome/pro-regular-svg-icons";
  import { _, user, theme, language, Auth } from "../../stores/index";
  import { buildDocsUrl, getDocsApps } from "../../utils/docs";
  import CookieFrame from "../../components/CookieFrame.svelte";

  /** Id of the app whose docs are shown; comes from `/docs/:appId`. */
  export let appId = undefined;

  const ICONS_BY_APP_TYPE = {
    "gl-access-manager": faShieldHalved,
    "gl-channel-manager": faTowerBroadcast,
    "gl-cut-manager": faScissors,
    "gl-graphics-manager": faPalette,
    "gl-workflow-manager": faDiagramProject,
  };

  // First-party docs need scripts and their own origin (search, theme storage).
  // On a host shared with the shell this only blocks top navigation.
  const DOCS_SANDBOX = [
    "allow-scripts",
    "allow-same-origin",
    "allow-popups",
    "allow-popups-to-escape-sandbox",
    "allow-downloads",
  ].join(" ");

  const config = getContext("config");
  const { isAuthorized } = new Auth(user);

  $: docsApps = getDocsApps($user?.apps, $isAuthorized);
  $: selectedApp = docsApps.find((app) => app.id === appId);
  $: if (!appId && docsApps.length > 0) {
    navigate(`/docs/${docsApps[0].id}`, { replace: true });
  }

  $: docsUrl =
    selectedApp &&
    buildDocsUrl(config.API_URL, selectedApp.id, {
      theme: $theme,
      lang: $language,
    });

  function openDocs(app) {
    navigate(`/docs/${app.id}`);
  }

  // MenuItem is a plain div, so Enter does not become a click on its own.
  function handleMenuKeydown(event, app) {
    if (event.key !== "Enter") return;

    openDocs(app);
  }
</script>

<svelte:head>
  <title>{$_("docs.title")} | {config.NAME}</title>
</svelte:head>

<div class="docs">
  <div class="sidebar theme-dark">
    <Menu>
      {#each docsApps as app (app.id)}
        <MenuItem
          selected={app.id === appId}
          icon={ICONS_BY_APP_TYPE[app.type] ?? faBook}
          on:click={() => openDocs(app)}
          on:keydown={(event) => handleMenuKeydown(event, app)}
        >
          <div class="label">{app.name}</div>
        </MenuItem>
      {/each}
    </Menu>
  </div>

  <main class="viewer">
    {#if selectedApp}
      <!-- The docs are authenticated by the session cookie, so the frame must
           be a CookieFrame: it is paused when another tab switches the contract. -->
      <CookieFrame
        src={docsUrl}
        title={`${selectedApp.name} – ${$_("docs.title")}`}
        referrerpolicy="strict-origin-when-cross-origin"
        sandbox={DOCS_SANDBOX}
      >
        <svelte:fragment slot="placeholder" let:state>
          {#if state === "unavailable"}
            <Callout status="warning" class="m-6">
              {$_("docs.sessionUnavailable")}
            </Callout>
          {/if}
        </svelte:fragment>
      </CookieFrame>
    {:else if docsApps.length === 0}
      <Callout status="info" class="m-6">{$_("docs.empty")}</Callout>
    {:else if appId}
      <Callout status="info" class="m-6">{$_("docs.unavailable")}</Callout>
    {/if}
  </main>
</div>

<style>
  .docs {
    display: flex;
    height: 100%;
  }

  .sidebar {
    flex: 0 0 262px;
    padding: 14px;
    overflow-y: auto;
    background-color: var(--gl-background);
  }

  .viewer {
    display: flex;
    flex: 1;
    min-width: 0;
  }

  /* rendered inside CookieFrame */
  .viewer :global(iframe) {
    flex: 1;
    border: 0;
  }

  @media screen and (max-width: 1300px) {
    .sidebar {
      flex-basis: 110px;
      padding: 0;
    }

    .sidebar :global(.gl-icon) {
      margin: 0;
    }

    .sidebar :global(.gl-menu-item) {
      flex-direction: column;
      text-align: center;
    }

    .label {
      font-size: 10px;
    }
  }
</style>
